/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, switchMap } from 'rxjs';
import { faBook } from '@fortawesome/free-solid-svg-icons/faBook';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';
import { faCommentSlash } from '@fortawesome/free-solid-svg-icons/faCommentSlash';
import { faSquare } from '@fortawesome/free-solid-svg-icons/faSquare';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { PayloadInterface } from '../../../models/interface/payload.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { AlertService } from '../../../services/alert/alert.service';
import { InterventionInstanceConfigurationInterface } from '../../../models/interface/intervention-instances/intervention-instance.configuration.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { LessonInterface } from '../../../models/interface/lesson.interface';
import { filter, take } from 'rxjs/operators';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { SkillInterface } from '../../../models/interface/skill.interface';
import { HelperSkillService } from '../../../services/helper/helper-skill/helper-skill.service';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { Store } from '@ngrx/store';
import { StudyActionTypes } from '../../../store/study/study.action';
import { DiaryStore } from '../../../store/diary/component-store/diary.store';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { SkillStore } from '../../../store/skill/component-store/skill.store';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

/**
 * Component:
 * Intervention creation confirmation displaying the configuration before assigning the intervention;
 */

@Component({
  selector: 'app-instance-creation-confirmation',
  templateUrl: './instance-creation-confirmation.component.html',
  styleUrls: ['./instance-creation-confirmation.component.scss'],
  providers: [DiaryStore, InterventionInstanceStore, SkillStore],
})
export class InstanceCreationConfirmationComponent implements OnInit, OnDestroy {
  // Icon
  faComment = faComment;
  faCommentSlash = faCommentSlash;
  faHandsHelping = faHandsHelping;
  faHands = faHands;
  faBook = faBook;
  faEye = faEye;
  faSquare = faSquare;

  public instancePayload: PayloadInterface = null;
  public instanceAttributes: {
    ecoach_ids: number[];
    patient_id: number;
    intervention_id: number;
    intervention_type: string;
    configuration: {
      questionnaire_configuration: InterventionInstanceConfigurationInterface[];
      custom_order: number[];
      starting_at: number;
    };
  };

  // MyUserId
  public myUserId: number;

  public studyMembers: UserInterface[] = [];
  public studyCollaborator: UserInterface[] = [];

  public formCustomOrder: number[] = [];
  public unconfirmedSubject = new BehaviorSubject<boolean>(true);

  public intervention: InterventionInterface;
  public questionnaires: LessonInterface[] = [];

  public diaries: DiaryInterface[] = [];
  public diariesOfStudy$: Observable<DiaryInterface[]>;

  public skills: SkillInterface[] = [];

  public skillsOfIntervention$: Observable<SkillInterface[]>;

  public reminderConfig: { payload: PayloadInterface; intervention: InterventionInterface };

  public defaultReminderText: Object;

  private createInstanceAsECoachResponse$: Observable<any>;

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;

  private positionType: string[] = ['-1'];

  private subscriptions: Subscription[] = [];

  public selectedLanguageSubject = new BehaviorSubject<string>('');

  private readonly instanceKey = 'intervention-instance-reminder';
  private readonly greetingKey = 'default_reminder_message_head';
  private readonly textKey = 'default_reminder_message';
  private readonly closingKey = 'default_reminder_closing';
  private readonly signatureKey = 'default_reminder_signature';

  constructor(
    private diaryStore: DiaryStore,
    private interventionInstanceStore: InterventionInstanceStore,
    private skillStore: SkillStore,
    private alertService: AlertService,
    private helperService: HelperService,
    private helperSkillService: HelperSkillService,
    private helperDialogService: HelperDialogService,
    private store: Store<{
      skills: SkillInterface[];
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
    }>,
    private translateService: TranslateService,
    private httpClient: HttpClient,
  ) {
    this.diariesOfStudy$ = this.diaryStore.diariesOfStudy$;
    this.createInstanceAsECoachResponse$ = this.interventionInstanceStore.createInstanceAsECoachResponse$;
    this.skillsOfIntervention$ = this.skillStore.skillsOfIntervention$;
    this.selectedLanguageSubject.next(this.translateService.currentLang);
    this.subscriptions.push(
      this.selectedLanguageSubject
        .pipe(switchMap((language) => this.httpClient.get(`../../../../assets/i18n/${language}.json`)))
        .subscribe((translation) => {
          this.defaultReminderText = translation;
        }),
    );
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  public get helperSkill() {
    return this.helperSkillService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  public get alert() {
    return this.alertService;
  }

  // Set study and its configuration from parent
  @Input() _intervention(_intervention: InterventionInterface) {
    this.intervention = _intervention;
  }

  // Set members of study from parent
  @Input() _members(_members: UserInterface[]) {
    this.studyMembers = _members;
  }

  // Set collaborators of study from parent
  @Input() _collaborators(_collaborators: UserInterface[]) {
    this.studyCollaborator = _collaborators;
  }

  @Input() _payload(_payload: PayloadInterface) {
    if (_payload) {
      this.instancePayload = _payload;
      this.instanceAttributes = _payload.data.attributes;
      this.formCustomOrder = this.instanceAttributes.configuration.custom_order;
    }
  }

  @Input() _questionnaires(_questionnaires: LessonInterface[]) {
    this.questionnaires = _questionnaires;
    // Indexing position of questionnaire
    this.questionnaires.forEach((_questionnaire, index) => {
      this.positionType.push(index.toString());
    });
  }

  @Input() _myUserId(_myUserId: number) {
    this.myUserId = _myUserId;
  }

  ngOnInit(): void {
    this.store.dispatch({
      type: StudyActionTypes.getCollaboratorsType,
      payload: { studyId: this.intervention.attributes.study_id, include: 'roles' },
    });

    this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(this.intervention.attributes.study_id));
    this.subscriptions.push(
      this.collaboratorsOfStudy$
        .pipe(
          filter(collaboratorsOfStudy => !!collaboratorsOfStudy),
          take(1),
        )
        .subscribe((result) => {
          this.studyCollaborator = result.collaborators;
        })
    );
    this.diaryStore.getDiariesOfStudy({ studyId: this.intervention.attributes.study_id });
    this.diariesOfStudy$.subscribe((result) => {
      this.diaries = result;
    });

    this.skillStore.getSkillsOfIntervention({ id: this.intervention.id });
    this.subscriptions.push(
      this.skillsOfIntervention$.subscribe((result) => {
        this.skills = result;
      }),
    );
  }

  getReminderGreeting(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.greetingKey];
    } else {
      return '';
    }
  }

  getReminderMessage(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.textKey];
    } else {
      return '';
    }
  }

  getReminderClosing(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.closingKey];
    } else {
      return '';
    }
  }

  getReminderSignature(): string {
    if (this.defaultReminderText) {
      return this.defaultReminderText[this.instanceKey][this.signatureKey];
    } else {
      return '';
    }
  }

  public getQuestionnaireById(questionnaireId: number): LessonInterface {
    const found = this.questionnaires.find(
      (questionnaire) => questionnaire.id.toString() === questionnaireId.toString(),
    );
    return found !== undefined ? found : null;
  }

  public getConfigurationByQuestionnaireId(questionnaireId: number): InterventionInstanceConfigurationInterface {
    const found = this.instanceAttributes.configuration.questionnaire_configuration.find(
      (config) => config.id.toString() === questionnaireId.toString(),
    );
    return found !== undefined ? found : null;
  }

  public createInstance(userId?: number): Observable<any> {
    this.instancePayload.data.attributes.ecoach_ids = this.instancePayload.data.attributes.ecoach_ids.filter(
      (id: number) => id.toString() !== this.myUserId.toString(),
    );
    if (!!userId) {
      this.instancePayload.data.attributes.patient_id = userId;
    }
    this.interventionInstanceStore.createInstanceAsECoach({ payload: this.instancePayload });
    return this.createInstanceAsECoachResponse$.pipe(
      filter(value => !!value),
      take(1),
    );
  }

  public showAfterPreviousUnlockHint(questionnaireId: number): boolean {
    const foundConfig = this.instanceAttributes.configuration.questionnaire_configuration.find(
      (config) => config.id.toString() === questionnaireId.toString(),
    );
    if (!foundConfig || foundConfig.unlock_type !== 'after_previous') {
      return false;
    }
    // Get configurations based on custom_order
    const currentPos = this.instanceAttributes.configuration.custom_order.findIndex(
      (value) => value.toString() === questionnaireId.toString(),
    );
    if (currentPos < 1) {
      return false;
    }
    const previousPos = currentPos - 1;
    const previousConfigId = this.instanceAttributes.configuration.custom_order[previousPos].toString();
    const predecessor = this.instanceAttributes.configuration.questionnaire_configuration.find(
      (config) => config.id.toString() === previousConfigId,
    );
    return predecessor !== undefined && predecessor.feedback_required;
  }

  public openDialogDiaryPreview(diary: DiaryInterface) {
    this.helperDialog
      .openDialogDiaryPreview(diary)
      .afterClosed()
      .subscribe(() => {});
  }

  public openDialogInterventionInstanceReminderCreation(reminder?: {
    payload: PayloadInterface;
    intervention: InterventionInterface;
  }): void {
    this.helperDialog
      .openDialogCreateInterventionInstanceReminder(undefined, undefined, reminder?.payload)
      .afterClosed()
      .subscribe((result) => {
        if (!(result instanceof HttpResponse) && !!result) {
          this.reminderConfig = { payload: result, intervention: this.intervention };
        }
      });
  }

  public removeReminderConfig(): void {
    this.reminderConfig = null;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
